// A venue's tax registration — its tax mode and GSTIN, a dated setting (F-PAY-11; spec/06 §10.3–§10.4)
// — as TanStack Query hooks over the generated client, `packages/contracts/src/routes/tax-registration.ts`:
//
//   GET  …/venues/{v}/tax-registration                     what is in force, scheduled, history   venues:read
//   POST …/venues/{v}/tax-registration                     record a dated change                  tax:manage
//   POST …/venues/{v}/tax-registration/{id}/withdraw       withdraw a change before its day       tax:manage
//
// `tax.mode` and `tax.gstin` never go through `PUT …/settings` (the server refuses them there); this
// is their only write. Each write answers the whole view, which replaces the cached one.

import type { ScheduleTaxRegistration, TaxRegistrationView } from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api/client';

/** The permission that records or withdraws a change (the route's `x-clavian-permission`; spec/07 §6.3). */
export const TAX_MANAGE = 'tax:manage';

export const taxRegistrationKey = (organizationId: string, venueId: string) =>
  ['venues', organizationId, venueId, 'tax-registration'] as const;

export function useTaxRegistration(organizationId: string, venueId: string) {
  const api = useApi();
  return useQuery<TaxRegistrationView>({
    queryKey: taxRegistrationKey(organizationId, venueId),
    queryFn: () => api.getVenueTaxRegistration({ path: { organizationId, venueId } }),
    refetchOnWindowFocus: false,
  });
}

function useStoreView(organizationId: string, venueId: string) {
  const queryClient = useQueryClient();
  return (view: TaxRegistrationView) => queryClient.setQueryData(taxRegistrationKey(organizationId, venueId), view);
}

/** Record a dated change of the venue's tax mode and GSTIN. Audited. */
export function useScheduleTaxRegistration(organizationId: string, venueId: string) {
  const api = useApi();
  const store = useStoreView(organizationId, venueId);
  return useMutation({
    mutationFn: (body: ScheduleTaxRegistration) => api.scheduleVenueTaxRegistration({ path: { organizationId, venueId }, body }),
    onSuccess: store,
  });
}

/** Withdraw a change dated later, before its business day begins. Audited. */
export function useWithdrawTaxRegistration(organizationId: string, venueId: string) {
  const api = useApi();
  const store = useStoreView(organizationId, venueId);
  return useMutation({
    mutationFn: (registrationId: string) => api.withdrawVenueTaxRegistration({ path: { organizationId, venueId, registrationId } }),
    onSuccess: store,
  });
}
